import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Truck, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import SocialIcons from "@/components/SocialIcons";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Pricing", path: "/pricing" },
  { label: "Blog", path: "/blog" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50"
    >
      {/* URGENCY BANNER */}
      <div data-testid="urgency-banner" className="bg-[#FF6B00] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-12 flex items-center justify-between h-9">
          <div className="flex items-center gap-2">
            <Zap className="w-3.5 h-3.5 text-yellow-200" fill="currentColor" strokeWidth={0} />
            <span className="text-xs sm:text-sm font-bold tracking-wide">Urgent job? 1-2 minutes response available today</span>
          </div>
          <a
  href="tel:+353874199541"
  onClick={() => window.gtag_report_conversion && window.gtag_report_conversion('tel:+353874199541')}
>
            <Button className="h-6 rounded-sm bg-white text-[#FF6B00] hover:bg-yellow-50 text-xs font-extrabold px-3 py-0 gap-1">
              <Phone className="w-3 h-3" /> Get Help Now
            </Button>
          </a>
        </div>
      </div>
      {/* MAIN NAV */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-black/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <div className="w-9 h-9 bg-[#0055FF] rounded-sm flex items-center justify-center">
            <Truck className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <span className="font-extrabold text-lg tracking-tight text-[#0A0A0A]">
            Go<span className="text-[#0055FF]">Vans</span><span className="text-[#475569] text-sm font-medium">.ie</span>
          </span>
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`text-sm font-medium transition-colors ${
                location.pathname === link.path
                  ? "text-[#0055FF]"
                  : "text-[#475569] hover:text-[#0A0A0A]"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <SocialIcons variant="light" className="mr-1" />
          <a
  href="tel:+353874199541"
  data-testid="nav-call-btn"
  onClick={() => window.gtag_report_conversion && window.gtag_report_conversion('tel:+353874199541')}
>
            <Button variant="outline" className="rounded-sm gap-2 border-black/10 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white">
              <Phone className="w-4 h-4" /> Call Now
            </Button>
          </a>
          <Link to="/contact" data-testid="nav-quote-btn">
            <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC] text-white text-sm font-semibold">
              Get a Quote
            </Button>
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-3 -mr-3"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          {open ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div id="mobile-menu" data-testid="mobile-menu" className="md:hidden bg-white border-t border-black/5 px-6 pb-6">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setOpen(false)}
              className={`block py-3 text-base font-medium border-b border-black/5 ${
                location.pathname === link.path ? "text-[#0055FF]" : "text-[#475569]"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex flex-col gap-3 mt-4">
            <a
  href="tel:+353874199541"
  onClick={() => window.gtag_report_conversion && window.gtag_report_conversion('tel:+353874199541')}
>
              <Button variant="outline" className="w-full rounded-sm gap-2 font-semibold">
                <Phone className="w-4 h-4" /> Call Now
              </Button>
            </a>
            <Link to="/contact" onClick={() => setOpen(false)}>
              <Button className="w-full rounded-sm bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold">
                Get a Quote
              </Button>
            </Link>
            <div className="pt-2 mt-1 border-t border-black/5 flex items-center justify-center">
              <SocialIcons variant="light" />
            </div>
          </div>
        </div>
      )}
      </div>
    </nav>
  );
}
